import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart3,
  Target,
  Clock,
  Package,
  CheckCircle2,
  XCircle,
  Calendar,
  Gauge,
  LineChart,
} from "lucide-react";
import { ExportButton } from "@/components/common/ExportButton";
import { workOrdersExportConfig, formatters } from "@/lib/exportUtils";
import { OEEDashboard } from "@/components/Reports/OEEDashboard";
import { ProductionTrends } from "@/components/Reports/ProductionTrends";
import { OrdersSummary } from "@/components/Reports/OrdersSummary";
import { QualityReport } from "@/components/Reports/QualityReport";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

export default function Reports() {
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState("30d");

  const getDateRange = () => {
    const today = new Date();
    switch (dateRange) {
      case "7d":
        return { start: subDays(today, 7), end: today };
      case "30d":
        return { start: subDays(today, 30), end: today };
      case "90d":
        return { start: subDays(today, 90), end: today };
      case "month":
        return { start: startOfMonth(today), end: endOfMonth(today) };
      default:
        return { start: subDays(today, 30), end: today };
    }
  };

  const { start, end } = getDateRange();

  // Fetch work orders stats
  const { data: workOrders } = useQuery({
    queryKey: ["work-orders-stats", dateRange],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select("*")
        .gte("created_at", start.toISOString())
        .lte("created_at", end.toISOString());

      if (error) throw error;
      return data;
    },
  });

  // Fetch production batches
  const { data: batches } = useQuery({
    queryKey: ["batches-stats", dateRange],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_batches")
        .select("*")
        .gte("created_at", start.toISOString())
        .lte("created_at", end.toISOString());

      if (error) throw error;
      return data;
    },
  });

  // Fetch quality inspections
  const { data: inspections } = useQuery({
    queryKey: ["inspections-stats", dateRange],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select("*")
        .gte("inspection_date", start.toISOString())
        .lte("inspection_date", end.toISOString());

      if (error) throw error;
      return data;
    },
  });

  // Fetch process records for time analysis
  const { data: processRecords } = useQuery({
    queryKey: ["process-records-stats", dateRange],
    queryFn: async () => {
      const { data, error } = await api
        .from("process_records")
        .select("*")
        .gte("created_at", start.toISOString())
        .lte("created_at", end.toISOString());

      if (error) throw error;
      return data;
    },
  });

  // Calculate KPIs
  const kpis = {
    totalOrders: workOrders?.length || 0,
    completedOrders: workOrders?.filter((o) => o.status === "completed").length || 0,
    inProgressOrders: workOrders?.filter((o) => o.status === "in_progress").length || 0,
    totalBatches: batches?.length || 0,
    completedBatches: batches?.filter((b) => b.status === "completed").length || 0,
    totalQuantity: batches?.reduce((sum, b) => sum + (b.quantity || 0), 0) || 0,
    totalInspections: inspections?.length || 0,
    passedInspections: inspections?.filter((i) => i.status === "passed").length || 0,
    failedInspections: inspections?.filter((i) => i.status === "failed").length || 0,
    qualityRate: inspections?.length
      ? Math.round(
          (inspections.filter((i) => i.status === "passed").length /
            inspections.filter((i) => i.status === "passed" || i.status === "failed").length) *
            100
        ) || 0
      : 0,
    orderCompletionRate: workOrders?.length
      ? Math.round(
          (workOrders.filter((o) => o.status === "completed").length / workOrders.length) * 100
        )
      : 0,
  };

  // Calculate average cycle time
  const completedProcesses = processRecords?.filter((p) => p.end_time) || [];
  const avgCycleTime =
    completedProcesses.length > 0
      ? completedProcesses.reduce((sum, p) => {
          const start = new Date(p.start_time).getTime();
          const end = new Date(p.end_time).getTime();
          return sum + (end - start);
        }, 0) /
        completedProcesses.length /
        60000 // Convert to minutes
      : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reportes y KPIs</h1>
            <p className="text-muted-foreground mt-1">
              Dashboard ejecutivo con metricas de produccion
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[180px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Ultimos 7 dias</SelectItem>
                <SelectItem value="30d">Ultimos 30 dias</SelectItem>
                <SelectItem value="90d">Ultimos 90 dias</SelectItem>
                <SelectItem value="month">Este mes</SelectItem>
              </SelectContent>
            </Select>
            <ExportButton
              data={workOrders}
              columns={workOrdersExportConfig}
              filename="reporte_ordenes"
              title="Reporte de Ordenes de Trabajo"
              subtitle="Athenea MES"
              dateRange={{ start, end }}
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-2xl grid-cols-5">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="oee" className="flex items-center gap-2">
              <Gauge className="w-4 h-4" />
              OEE
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2">
              <LineChart className="w-4 h-4" />
              Tendencias
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Ordenes
            </TabsTrigger>
            <TabsTrigger value="quality" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Calidad
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Main KPIs */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Ordenes Completadas</p>
                      <p className="text-3xl font-bold">{kpis.completedOrders}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        de {kpis.totalOrders} totales
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-green-500/10">
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>Tasa de completado</span>
                      <span className="font-medium">{kpis.orderCompletionRate}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${kpis.orderCompletionRate}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Unidades Producidas</p>
                      <p className="text-3xl font-bold">{kpis.totalQuantity.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        en {kpis.completedBatches} lotes
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-blue-500/10">
                      <Package className="w-6 h-6 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Tasa de Calidad</p>
                      <p className="text-3xl font-bold">{kpis.qualityRate}%</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {kpis.passedInspections} aprobadas
                      </p>
                    </div>
                    <div
                      className={cn(
                        "p-3 rounded-full",
                        kpis.qualityRate >= 95
                          ? "bg-green-500/10"
                          : kpis.qualityRate >= 85
                          ? "bg-yellow-500/10"
                          : "bg-red-500/10"
                      )}
                    >
                      <Target
                        className={cn(
                          "w-6 h-6",
                          kpis.qualityRate >= 95
                            ? "text-green-500"
                            : kpis.qualityRate >= 85
                            ? "text-yellow-500"
                            : "text-red-500"
                        )}
                      />
                    </div>
                  </div>
                  {kpis.failedInspections > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-red-500">
                      <XCircle className="w-4 h-4" />
                      {kpis.failedInspections} rechazadas
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Tiempo Ciclo Promedio</p>
                      <p className="text-3xl font-bold">{avgCycleTime.toFixed(1)}</p>
                      <p className="text-sm text-muted-foreground mt-1">minutos por proceso</p>
                    </div>
                    <div className="p-3 rounded-full bg-purple-500/10">
                      <Clock className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Orders by Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Estado de Ordenes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span>Pendientes</span>
                      </div>
                      <span className="font-bold">
                        {workOrders?.filter((o) => o.status === "pending").length || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span>En Progreso</span>
                      </div>
                      <span className="font-bold">{kpis.inProgressOrders}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span>Completadas</span>
                      </div>
                      <span className="font-bold">{kpis.completedOrders}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span>Canceladas</span>
                      </div>
                      <span className="font-bold">
                        {workOrders?.filter((o) => o.status === "cancelled").length || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Production Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resumen de Produccion</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Lotes Iniciados</span>
                      <span className="font-bold">{kpis.totalBatches}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Lotes Completados</span>
                      <span className="font-bold text-green-600">{kpis.completedBatches}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Lotes en Proceso</span>
                      <span className="font-bold text-blue-600">
                        {batches?.filter((b) => b.status === "in_progress").length || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Inspecciones Realizadas</span>
                      <span className="font-bold">{kpis.totalInspections}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Procesos Registrados</span>
                      <span className="font-bold">{processRecords?.length || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Period Info */}
            <Card className="bg-muted/30">
              <CardContent className="py-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Periodo:</span>
                    <span className="font-medium">
                      {format(start, "dd MMM yyyy", { locale: es })} -{" "}
                      {format(end, "dd MMM yyyy", { locale: es })}
                    </span>
                  </div>
                  <Badge variant="outline">
                    {Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))} dias
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="oee" className="mt-6">
            <OEEDashboard dateRange={{ start, end }} />
          </TabsContent>

          <TabsContent value="trends" className="mt-6">
            <ProductionTrends dateRange={{ start, end }} />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <OrdersSummary dateRange={{ start, end }} />
          </TabsContent>

          <TabsContent value="quality" className="mt-6">
            <QualityReport dateRange={{ start, end }} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
